import React, { useState, useEffect } from 'react';
import { Panel, Button } from '../../design/primitives';
import { COLORS, SPACING, TYPOGRAPHY } from '../../design/tokens';
import { apiClient } from '../../api/apiConfig';
import { MetricChart, Spinner } from '../ui';
import { CashFlowMonth } from '../../types/finance';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { format } from 'date-fns';

const formatCurrency = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

const getJanOfYear = () => format(new Date(), 'yyyy') + '-01';

const formatMonthLabel = (yyyymm: string) => {
  const [year, month] = yyyymm.split('-');
  const date = new Date(parseInt(year), parseInt(month) - 1);
  return format(date, 'MMM yyyy');
};

const shiftMonth = (yyyymm: string, delta: number): string => {
  const [year, month] = yyyymm.split('-').map(Number);
  const d = new Date(year, month - 1 + delta);
  return format(d, 'yyyy-MM');
};

const PRESETS = [
  { label: '3M', offset: -2 },
  { label: '6M', offset: -5 },
  { label: 'YTD', offset: 0 },
  { label: '1Y', offset: -11 },
];

const CashFlowView: React.FC = () => {
  const [data, setData] = useState<CashFlowMonth[]>([]);
  const [loading, setLoading] = useState(false);
  const [startMonth, setStartMonth] = useState(getJanOfYear());

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await apiClient.get('/api/transactions/cash_flow/', {
          params: { start_month: startMonth },
        });
        setData(res.data);
      } catch (err) {
        console.error('Failed to fetch cash flow', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [startMonth]);

  const applyPreset = (offset: number) => {
    if (offset === 0) {
      // YTD
      setStartMonth(getJanOfYear());
    } else {
      const now = new Date();
      const d = new Date(now.getFullYear(), now.getMonth() + offset);
      setStartMonth(format(d, 'yyyy-MM'));
    }
  };

  const totalIncome = data.reduce((s, d) => s + d.income, 0);
  const totalExpenses = data.reduce((s, d) => s + d.expenses, 0);
  const netSavings = totalIncome - totalExpenses;
  const savingsRate = totalIncome > 0 ? (netSavings / totalIncome) * 100 : 0;

  const chartData = data.map(d => {
    const [y, m] = d.month.split('-');
    const dt = new Date(parseInt(y), parseInt(m) - 1);
    return {
      date: dt.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
      income: d.income,
      expenses: d.expenses,
    };
  });

  const arrowStyle: React.CSSProperties = {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: SPACING[4],
    display: 'inline-flex',
    alignItems: 'center',
    color: COLORS.text.secondary,
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[16] }}>
      {/* Controls row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[16], flexWrap: 'wrap' }}>
        {/* Start month picker */}
        <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[4] }}>
          <span style={{ fontSize: TYPOGRAPHY.size.sm, color: COLORS.text.secondary, marginRight: SPACING[4] }}>From</span>
          <button style={arrowStyle} onClick={() => setStartMonth(m => shiftMonth(m, -1))}>
            <ChevronLeftIcon style={{ width: '16px', height: '16px' }} />
          </button>
          <span style={{
            fontSize: TYPOGRAPHY.size.sm,
            fontWeight: TYPOGRAPHY.weight.medium,
            color: COLORS.text.primary,
            minWidth: '90px',
            textAlign: 'center',
          }}>
            {formatMonthLabel(startMonth)}
          </span>
          <button style={arrowStyle} onClick={() => setStartMonth(m => shiftMonth(m, 1))}>
            <ChevronRightIcon style={{ width: '16px', height: '16px' }} />
          </button>
        </div>

        {/* Presets */}
        <div style={{ display: 'flex', gap: SPACING[4] }}>
          {PRESETS.map(p => (
            <Button
              key={p.label}
              variant="ghost"
              onClick={() => applyPreset(p.offset)}
              style={{ padding: `${SPACING[4]} ${SPACING[12]}` }}
            >
              {p.label}
            </Button>
          ))}
        </div>
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: SPACING[32] }}>
          <Spinner size="md" />
        </div>
      ) : (
        <>
          {/* Chart */}
          <Panel>
            <MetricChart
              data={chartData}
              series={[
                { dataKey: 'income', name: 'Income', metricType: 'currency', color: COLORS.status.success, chartType: 'bar' },
                { dataKey: 'expenses', name: 'Expenses', metricType: 'currency', color: COLORS.status.error, chartType: 'bar' },
              ]}
              type="bar"
              height={300}
            />
          </Panel>

          {/* Summary cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: SPACING[12] }}>
            <Panel>
              <div style={{ fontSize: TYPOGRAPHY.body.sizes.small, color: COLORS.text.secondary, marginBottom: SPACING[4] }}>
                Total Income
              </div>
              <div style={{
                fontSize: TYPOGRAPHY.headings.sizes.h2,
                fontFamily: TYPOGRAPHY.headings.fontFamily,
                fontWeight: '700',
                color: COLORS.status.success,
              }}>
                {formatCurrency(totalIncome)}
              </div>
            </Panel>
            <Panel>
              <div style={{ fontSize: TYPOGRAPHY.body.sizes.small, color: COLORS.text.secondary, marginBottom: SPACING[4] }}>
                Total Expenses
              </div>
              <div style={{
                fontSize: TYPOGRAPHY.headings.sizes.h2,
                fontFamily: TYPOGRAPHY.headings.fontFamily,
                fontWeight: '700',
                color: COLORS.status.error,
              }}>
                {formatCurrency(totalExpenses)}
              </div>
            </Panel>
            <Panel>
              <div style={{ fontSize: TYPOGRAPHY.body.sizes.small, color: COLORS.text.secondary, marginBottom: SPACING[4] }}>
                Net Savings
              </div>
              <div style={{
                fontSize: TYPOGRAPHY.headings.sizes.h2,
                fontFamily: TYPOGRAPHY.headings.fontFamily,
                fontWeight: '700',
                color: netSavings >= 0 ? COLORS.status.success : COLORS.status.error,
              }}>
                {formatCurrency(netSavings)}
              </div>
            </Panel>
            <Panel>
              <div style={{ fontSize: TYPOGRAPHY.body.sizes.small, color: COLORS.text.secondary, marginBottom: SPACING[4] }}>
                Savings Rate
              </div>
              <div style={{
                fontSize: TYPOGRAPHY.headings.sizes.h2,
                fontFamily: TYPOGRAPHY.headings.fontFamily,
                fontWeight: '700',
                color: savingsRate >= 0 ? COLORS.status.success : COLORS.status.error,
              }}>
                {savingsRate.toFixed(1)}%
              </div>
            </Panel>
          </div>
        </>
      )}
    </div>
  );
};

export default CashFlowView;
